/**
 * WHAT CHANGED ON A KEEPER SLATE, in the words a manager would use.
 *
 * Tim, 31 August: "need some more detail in the logging. I believe this interaction from Amit is
 * that he added Jonathan Brooks to his keepers. Need that detail."
 *
 * The log said `Submitted 6 keepers for 2026` and, on the row he opened, `Opened keepers/slate`.
 * Both true, neither useful: the first is a COUNT and the second is a page view. Reading them
 * back, the one question anybody actually has — what did he change — could only be answered by
 * remembering what the slate looked like before.
 *
 * WHY THIS CANNOT BE DERIVED. `domain/auditLog.ts` builds most of the log out of the records
 * themselves, which is why it reaches back years and cannot be falsified. But a declaration
 * document holds only its CURRENT state; the version before this save is gone the moment the save
 * lands. A diff therefore has to be written AS IT HAPPENS, from the screen that knows both sides.
 *
 * Kept out of the component so it can be tested without a browser, and so the sentence the log
 * shows is decided in one place rather than assembled inside JSX.
 */

export interface SlateKeeper {
  player: string;
  franchiseTagged?: boolean;
  elevatedTo?: number | null;
  farmForfeited?: boolean;
}

export interface SlateChange {
  added: string[];
  removed: string[];
  /** Newly carrying the franchise tag. */
  tagged: string[];
  /** No longer carrying it. */
  untagged: string[];
  /** Player → the round he was moved to, when a hand-placed pick changed. */
  moved: Array<{ player: string; to: number }>;
  /** Nothing on the slate itself moved. A status change may still have. */
  quiet: boolean;
}

const key = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

export function slateChange(
  before: readonly SlateKeeper[], after: readonly SlateKeeper[],
): SlateChange {
  const was = new Map(before.map((k) => [key(k.player), k]));
  const now = new Map(after.map((k) => [key(k.player), k]));

  const added = after.filter((k) => !was.has(key(k.player))).map((k) => k.player);
  const removed = before.filter((k) => !now.has(key(k.player))).map((k) => k.player);
  const tagged: string[] = [];
  const untagged: string[] = [];
  const moved: Array<{ player: string; to: number }> = [];

  for (const k of after) {
    const old = was.get(key(k.player));
    if (!old) continue;
    if (Boolean(k.franchiseTagged) && !old.franchiseTagged) tagged.push(k.player);
    if (!k.franchiseTagged && Boolean(old.franchiseTagged)) untagged.push(k.player);
    if (k.elevatedTo && k.elevatedTo !== old.elevatedTo) {
      moved.push({ player: k.player, to: k.elevatedTo });
    }
  }

  return {
    added,
    removed,
    tagged,
    untagged,
    moved,
    quiet: added.length + removed.length + tagged.length + untagged.length + moved.length === 0,
  };
}

/** `a, b and c`. */
export function andList(xs: readonly string[]): string {
  if (xs.length <= 1) return xs[0] ?? '';
  if (xs.length === 2) return `${xs[0]} and ${xs[1]}`;
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

/**
 * The log's headline, which has to survive being read a month later with no context.
 *
 * NAMES, NOT COUNTS, and the count comes after them. "Added Jonathan Brooks · 6 keepers" answers
 * the question on sight; "Submitted 6 keepers" is what sent Tim looking through the log in the
 * first place. A slate with nothing but a status change says that instead of inventing a change.
 */
export function slateSaid(
  c: SlateChange, opts: { submitted: boolean; total: number; first?: boolean },
): string {
  const verb = opts.submitted ? 'Submitted' : 'Saved';
  const parts: string[] = [];
  if (opts.first && c.removed.length === 0) {
    parts.push(`First slate — ${andList(c.added)}`);
  } else {
    if (c.added.length) parts.push(`added ${andList(c.added)}`);
    if (c.removed.length) parts.push(`dropped ${andList(c.removed)}`);
  }
  if (c.tagged.length) parts.push(`franchise tag on ${andList(c.tagged)}`);
  if (c.untagged.length) parts.push(`tag off ${andList(c.untagged)}`);
  for (const m of c.moved) parts.push(`${m.player} moved to round ${m.to}`);

  const tail = `${opts.total} keeper${opts.total === 1 ? '' : 's'}`;
  if (parts.length === 0) {
    return opts.submitted ? `Submitted his slate unchanged — ${tail}` : `Saved a draft — ${tail}`;
  }
  /* The first clause carries the capital; the rest run on so the line reads as one sentence. */
  const said = parts.join(', ');
  return `${verb} — ${said.charAt(0).toUpperCase()}${said.slice(1)} · ${tail}`;
}
